import { Sentry } from '$lib/services/sentry';
import { browser } from '$app/environment';
import { getDeviceId } from '$lib/utils/device-id';
import { resetMasterExperiments } from '$lib/stores/experiments/store';
import { get, writable } from 'svelte/store';
import { ACCOUNT_STATUS, ACCOUNT_TYPES } from './constants';

/**
 * @typedef UserStoreState
 * @property {boolean} tokenValid
 * @property {string} token
 * @property {boolean} legacyExperience
 * @property {ACCOUNT_STATUS} accountStatus
 * @property {ACCOUNT_TYPES} accountType
 * @property {boolean} hasCredentials
 * @property {string} userToProductUuid
 * @property {string} paymentProvider
 * @property {any} userMetaData
 * @property {string} maskedEmail
 * @property {string} tmpEmail
 * @property {string} tmpPhone
 * @property {boolean} debugUser
 * @property {boolean} isReactivated
 * @property {string|null} error
 * @property {AoflAccount[]} aoflProducts
 */

/**
 * @typedef AoflAccount
 * @property {string} aoflProductUuid
 * @property {string} userToProductUuid
 * @property {boolean} originating
 * @property {boolean} activeSubscription
 * @property {string} paymentProvider
 * @property {string} accountType
 * @property {"primary"|"auxiliary"|"max"|null} subscriptionPlanCategory
 * @property {string} subscriptionUuid
 */

/**
 * @typedef {{[key:string]: string}} ApiResponse
 * @property {boolean} token_valid
 * @property {string} token
 * @property {boolean} legacy_experience
 * @property {string} account_status
 * @property {string} account_type
 * @property {boolean} has_credentials
 * @property {string} user_to_product_uuid
 * @property {string} payment_provider
 * @property {object} user_meta_data
 * @property {string} masked_email
 * @property {string} tmp_email
 * @property {boolean} debug_user
 * @property {boolean} is_reactivated
 * @property {AoflAccount[]} aofl_products
 */

/**
 * Converts known keys to camelCase and any unknown keys as well.
 * @param {ApiResponse} obj - The object to convert.
 * @param {{[key:string]: string}} keyMap - The mapping of known keys.
 * @returns {Partial<UserStoreState>} - The object with camelCase keys.
 */
const toCamelCase = (obj, keyMap) => {
	/** @type {Record<string, any>} */
	const camelCaseObj = {};

	for (const key in obj) {
		if (keyMap[key]) {
			camelCaseObj[keyMap[key]] = obj[key];
		} else {
			const camelCaseKey = key.replace(/_([a-z])/g, (match, p1) => p1.toUpperCase());
			camelCaseObj[camelCaseKey] = obj[key];
		}
	}

	return /** @type {Partial<UserStoreState>} */ (camelCaseObj);
};

/** @type {UserStoreState} */
const initialState = {
	tokenValid: false,
	token: '',
	legacyExperience: false,
	accountStatus: '',
	userToProductUuid: '',
	paymentProvider: '',
	userMetaData: {},
	maskedEmail: '',
	tmpEmail: browser ? sessionStorage?.getItem('tmpEmail') || '' : '',
	tmpPhone: browser ? sessionStorage?.getItem('tmpPhone') || '' : '',
	debugUser: false,
	error: null,
	isReactivated: false,
	aoflProducts: [],
	hasCredentials: false,
};

/**
 * User store.
 * State is fresh on each page load and validated through centralized initialization.
 * @type {import('svelte/store').Writable<UserStoreState>} - user store
 */
export const user = writable(initialState);

/**
 * Updates the store with successful data.
 * @param {ApiResponse} data - The data to update the store with.
 */
export const publishSuccess = (data) => {
	const sanitizedData = { ...data };
	delete sanitizedData.password;

	// TODO: clean up so unknown keys are still available in the store.
	const knownKeys = {
		token_valid: 'tokenValid',
		token: 'token',
		legacy_experience: 'legacyExperience',
		account_status: 'accountStatus',
		account_type: 'accountType',
		user_to_product_uuid: 'userToProductUuid',
		payment_provider: 'paymentProvider',
		user_meta_data: 'userMetaData',
		masked_email: 'maskedEmail',
		tmp_email: 'tmpEmail',
		tmpPhone: 'tmpPhone',
		debug_user: 'debugUser',
		is_reactivated: false,
		has_credentials: 'hasCredentials',
	};

	// Map aofl_products (array of snake_case objects) to camelCase into aoflAccounts
	/** @type {Array<AoflAccount>} */
	let aoflProducts = [];
	if (Array.isArray(sanitizedData?.aofl_products)) {
		aoflProducts = sanitizedData.aofl_products.map((acc) => ({
			aoflProductUuid: acc.aofl_product_uuid,
			userToProductUuid: acc.user_to_product_uuid,
			originating: acc.originating === true,
			activeSubscription: acc.active_subscription === true,
			paymentProvider: acc.payment_provider,
			accountType: acc.account_type,
			subscriptionPlanCategory: acc.subscription_plan_category,
			subscriptionUuid: acc.subscription_uuid,
		}));
	}

	const userData = {
		...toCamelCase(sanitizedData, knownKeys),
		...(aoflProducts.length ? { aoflProducts: aoflProducts } : {}),
	};

	// Account-based experiment assignments are bucketed by userToProductUuid and
	// must not carry over to a different account in the same JS session.
	const previousUserToProductUuid = get(user).userToProductUuid;
	if (
		previousUserToProductUuid &&
		userData.userToProductUuid &&
		userData.userToProductUuid !== previousUserToProductUuid
	) {
		resetMasterExperiments();
	}

	user.update((state) => ({ ...state, ...userData, error: null }));

	delete userData.tmpEmail;

	const sentryUser = {
		id: userData.userToProductUuid ? userData.userToProductUuid : getDeviceId(),
		...userData,
	};
	Sentry.setUser(sentryUser);
};

/**
 * Updates the store with an error.
 * @param {Error} error - The error object.
 */
export const publishFailure = (error) => {
	user.update((state) => ({
		...state,
		error: error.cause,
	}));
};

export const resetStore = () => {
	user.update((state) => ({
		...state,
		...initialState,
	}));
	resetMasterExperiments();

	Sentry.setUser(null);
};
